import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import {
  CreateTacticReqData,
  keys as tacticKeys,
  useCreateTactic,
} from './tacticHooks';
import { keys as workAssignmentKeys } from './workAssignmentHooks';

import { useActualIncidentId } from './commonHooks';
import { TacticBase } from '../../../types/iapBaseTypes';
import { IapScope, INCIDENT } from '../ICSConstants';

export type IncidentTactic = TacticBase & {
  id: UUID;
  tactic_id: UUID;
  incident_tactic_id: UUID;
  incident_id: UUID;
  user_guid: UUID;
  version: number;
  timestamp: Date;
  archived: boolean;
  work_assignments: UUID[];
  incident_resources: UUID[];
  user_name: string;
} & {
  strategies: UUID[];
};

export const keys = {
  base: 'incident_tactics',
  get all() {
    return [keys.base];
  },
  list: (incidentId: UUID, userId: UUID) => [keys.base, incidentId, userId],
} as const;

export const useIncidentTactics = (scope: IapScope) => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<IncidentTactic[]>(
        `${config.apiGateway.ics_incident_tactics}/${incidentId}`
      );
      return resp.data;
    },
    enabled: scope === INCIDENT,
  });
};

/** NOTE: this posts to the group tactic which will create the incident version. */
export const useCreateIncidentTactic = () => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const mutation = useCreateTactic();
  return {
    ...mutation,
    mutateAsync: (data: CreateTacticReqData) => {
      data.incident_id = incidentId;
      data.incident_resources = data.incident_resources ?? [];
      return mutation.mutateAsync(data, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: tacticKeys.all });
          queryClient.invalidateQueries({ queryKey: [keys.base] });
        },
      });
    },
  };
};

interface EditIncidentTacticReqData extends TacticBase {
  tactic_id: UUID;
  archived: boolean;
  strategies: UUID[];
  work_assignments: UUID[];
  incident_resources: UUID[];
}

export const useEditIncidentTactic = (tacticId?: UUID) => {
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditIncidentTacticReqData) => {
      const id = tacticId || data.tactic_id;
      return await axios.put(
        `${config.apiGateway.ics_incident_tactics}/${incidentId}/${id}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
      queryClient.invalidateQueries({
        queryKey: [workAssignmentKeys.base],
      });
      queryClient.invalidateQueries({
        queryKey: tacticKeys.all,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
